/**
 * The client portal's clean addresses on its own host (portal.cortexlayer.io).
 *
 * There the portal is the whole site, so its pages drop the /portal prefix: /tickets, /login,
 * and its home is /. Inside the app every portal route, link and redirect still says
 * /portal/...: these two functions translate at the edge (the router's history, the 401
 * reload, the sign-in redirect), so no page has to know which host it is on. On the staff host
 * nothing is translated and /portal/... is the address, as before.
 *
 * The server keeps the same rule (App\Support\PortalUrl::clean) for the links it emails.
 */

/**
 * First path segments that are not portal pages: the proposal, contract and invoice links,
 * oauth, the Allow page of a client's plugin sign-in (spec 012), which the server answers itself,
 * and connect, where a client types CortexTrace's code (spec 012).
 */
const PUBLIC_PAGES = new Set(['p', 'c', 'i', 'oauth', 'connect'])

/** True when the server said this page was served on the portal's own host. */
export function onPortalHost() {
    return typeof document !== 'undefined'
        && document.querySelector('meta[name="cortex-portal-host"]') !== null
}

function split(full) {
    const at = full.search(/[?#]/)
    return at === -1 ? [full, ''] : [full.slice(0, at), full.slice(at)]
}

/** An app address (/portal/tickets) as the portal host shows it (/tickets). */
export function toPortalAddress(full) {
    const [path, rest] = split(full)
    if (path === '/portal' || path === '/portal/' || path === '/portal/dashboard') return '/' + rest
    if (path.startsWith('/portal/')) return path.slice('/portal'.length) + rest
    return full
}

/** An address on the portal host (/tickets) as the app routes it (/portal/tickets). */
export function fromPortalAddress(full) {
    const [path, rest] = split(full)
    const first = path.split('/')[1] ?? ''
    if (first === 'portal' || PUBLIC_PAGES.has(first)) return full
    if (path === '/' || path === '') return '/portal/dashboard' + rest
    return '/portal' + path + rest
}

/**
 * The router's history on the portal host: the address bar shows clean addresses while the
 * routes keep their /portal paths. Wraps createWebHistory's object; nothing else changes.
 */
export function portalHostHistory(inner) {
    return {
        get base() { return inner.base },
        get location() { return fromPortalAddress(inner.location) },
        get state() { return inner.state },
        push: (to, data) => inner.push(toPortalAddress(to), data),
        replace: (to, data) => inner.replace(toPortalAddress(to), data),
        go: (delta, triggerListeners) => inner.go(delta, triggerListeners),
        listen: callback => inner.listen((to, from, info) => callback(fromPortalAddress(to), fromPortalAddress(from), info)),
        createHref: location => inner.createHref(toPortalAddress(location)),
        destroy: () => inner.destroy(),
    }
}
